import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import TrabajoForm from './TrabajoForm';

const EditTrabajoModal = ({ isOpen, trabajo, onClose, onSaved, weekDates = [] }) => {
  const [productos, setProductos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [piezasWarning, setPiezasWarning] = useState('');
  const [showMasOpciones, setShowMasOpciones] = useState(true); // edit: expandido por defecto

  const [form, setForm] = useState({
    fecha: '',
    cantidad_piezas: 1,
    producto_id: '',
    empleado_id: '',
    prioridad: 'NORMAL',
    tipo_corte: 'NORMAL',
    formato_corte: 'ENTERO',
    quiere_huesos: false,
    guardar_merma: false,
    observaciones: '',
  });

  useEffect(() => {
    if (isOpen && trabajo) {
      setForm({
        fecha: trabajo?.fecha || '',
        cantidad_piezas: trabajo?.cantidad_piezas || 1,
        producto_id: trabajo?.producto_id || '',
        empleado_id: trabajo?.empleado_id || '',
        prioridad: trabajo?.prioridad || 'NORMAL',
        tipo_corte: trabajo?.tipo_corte || 'NORMAL',
        formato_corte: trabajo?.formato_corte || 'ENTERO',
        quiere_huesos: trabajo?.quiere_huesos || false,
        guardar_merma: trabajo?.guardar_merma || false,
        observaciones: trabajo?.observaciones || '',
      });
      setError('');
      setPiezasWarning('');
      setShowMasOpciones(true);
      loadData();
    }
  }, [isOpen, trabajo]);

  const loadData = async () => {
    try {
      const [prodsResult, empsResult] = await Promise.all([
        supabase?.from('productos')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
        supabase?.from('empleados')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
      ]);
      setProductos(prodsResult?.data || []);
      setEmpleados(empsResult?.data || []);
    } catch (err) {
      console.error('[EditTrabajoModal.loadData]', err);
    }
  };

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setError('');

    // Advertencia al reducir piezas
    if (key === 'cantidad_piezas') {
      const piezas = trabajo?.piezas || [];
      const activePiezas = piezas?.filter(p => p?.estado !== 'PENDIENTE');
      if (value < piezas?.length && activePiezas?.length > 0) {
        if (value < activePiezas?.length) {
          setPiezasWarning(`No se pueden eliminar piezas activas. Hay ${activePiezas?.length} piezas en proceso o completadas.`);
        } else {
          setPiezasWarning(`Se eliminarán ${piezas?.length - value} piezas pendientes.`);
        }
      } else {
        setPiezasWarning('');
      }
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setError('');

    if (!form?.producto_id) {
      setError('El producto es obligatorio');
      return;
    }
    if (!form?.fecha) {
      setError('La fecha es obligatoria');
      return;
    }
    if ((form?.cantidad_piezas || 1) < 1) {
      setError('La cantidad debe ser al menos 1');
      return;
    }

    // No reducir por debajo de piezas activas
    const piezas = trabajo?.piezas || [];
    const activePiezas = piezas?.filter(p => p?.estado !== 'PENDIENTE');
    if (form?.cantidad_piezas < activePiezas?.length) {
      setError(`No se puede reducir a ${form?.cantidad_piezas} piezas. Hay ${activePiezas?.length} piezas activas.`);
      return;
    }

    setSaving(true);
    try {
      // Normalizar UUIDs vacíos a null antes de enviar a Supabase
      const cleanedForm = {
        ...form,
        empleado_id: form?.empleado_id && form?.empleado_id !== '' ? form?.empleado_id : null,
        producto_id: form?.producto_id && form?.producto_id !== '' ? form?.producto_id : null,
        configuracion_origen_id: form?.configuracion_origen_id && form?.configuracion_origen_id !== '' ? form?.configuracion_origen_id : null,
      };
      await onSaved(trabajo?.id, cleanedForm);
      onClose();
    } catch (err) {
      console.error('[EditTrabajoModal.handleSave]', err);
      setError(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !trabajo) return null;

  const piezas = trabajo?.piezas || [];
  const activePiezasCount = piezas?.filter(p => p?.estado !== 'PENDIENTE')?.length;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 overflow-hidden max-h-[90vh] flex flex-col" style={{ background: '#111111' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 flex-shrink-0">
          <div>
            <h2 className="font-oranienbaum font-bold text-white text-xl">Editar Trabajo</h2>
            <p className="text-white/40 text-xs font-oranienbaum mt-0.5">{trabajo?.cliente?.nombre}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <TrabajoForm
            mode="edit"
            form={form}
            onChange={handleChange}
            productos={productos}
            empleados={empleados}
            weekDates={weekDates}
            piezasWarning={piezasWarning}
            activePiezasCount={activePiezasCount}
            validationError={error}
            showMasOpciones={showMasOpciones}
            onToggleMasOpciones={() => setShowMasOpciones(v => !v)}
            onGuardarHabitual={() => {}}
          />
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-white/8 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-white/60 font-oranienbaum hover:bg-white/5 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-2 px-6 py-3 rounded-xl font-oranienbaum font-bold text-black transition-all disabled:opacity-50"
            style={{ background: '#D6EF4E', flexGrow: 2 }}
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTrabajoModal;
